import React, { useCallback, useMemo, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import Select, { MultiValue } from "react-select";
import { useInvestorDatabaseMetadata } from "../../hooks/use-investor-database-metadata";
import { mapStartupStatusToText } from "../../lib/map-startup-status-to-text";
import { updateProject } from "../../services/projectService";
import { Project } from "../../types/Project";
import { StartupStatus } from "../../types/StartupStatus";
import { StartupStatusOptions } from "../StartupStatusForm/StartupStatusForm";
import "./EditProjectModal.scss";

interface EditProjectModalProps {
  show: boolean;
  project: Project;
  onHide: () => void;
  onUpdate: () => void;
}

const EditProjectModal: React.FC<EditProjectModalProps> = ({
  show,
  project,
  onHide,
  onUpdate,
}) => {
  const { vcDirectoryMetadata, isLoading } = useInvestorDatabaseMetadata();

  const [selectedStatuses, setSelectedStatuses] = useState<StartupStatus[]>(
    project.startupStatus ?? [],
  );
  const [selectedSectors, setSelectedSectors] = useState<string[]>(
    project.sectors ?? [],
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setError(null);
      setIsSaving(true);

      try {
        await updateProject(project.id, {
          sectors: selectedSectors,
          startupStatus: selectedStatuses,
        });
        onUpdate();
        onHide();
      } catch (err) {
        setError("Failed to save project. Please try again.");
      } finally {
        setIsSaving(false);
      }
    },
    [onHide, onUpdate, project, selectedSectors, selectedStatuses],
  );

  const handleClose = () => {
    if (!isSaving) {
      setError(null);
      onHide();
    }
  };

  const handleStatusCheckboxChange = (status: StartupStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    );
  };

  const handleSectorChange = (
    selectedOptions: MultiValue<{ value: string; label: string }>,
  ) => {
    setSelectedSectors(selectedOptions.map((option) => option.value));
  };

  const sectorOptions = useMemo(() => {
    return (
      vcDirectoryMetadata?.sectorFocus.map((sector) => ({
        value: sector,
        label: sector,
      })) ?? []
    );
  }, [vcDirectoryMetadata]);

  return (
    <Modal
      className="basic-modal edit-project-modal"
      show={show}
      onHide={handleClose}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit Project</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          {error && <Alert variant="danger">{error}</Alert>}

          <Form.Group controlId="projectSectors">
            <Form.Label>Sectors (Select up to 6)</Form.Label>
            <Select
              isMulti
              options={sectorOptions}
              value={sectorOptions.filter((option) =>
                selectedSectors.includes(option.value),
              )}
              onChange={handleSectorChange}
              isOptionDisabled={() => selectedSectors.length >= 6}
              className="react-select-container"
              classNamePrefix="react-select"
              isDisabled={isLoading || isSaving}
            />
          </Form.Group>
          <Form.Group controlId="startupStatus">
            <Form.Label>Status</Form.Label>
            {StartupStatusOptions.map((status) => (
              <div key={status} className="checkbox-group">
                <Form.Check
                  type="checkbox"
                  label={mapStartupStatusToText(status)}
                  id={`status-${status}`}
                  value={status}
                  checked={selectedStatuses.includes(status)}
                  onChange={() => handleStatusCheckboxChange(status)}
                  disabled={isLoading}
                />
              </div>
            ))}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={isSaving}>
          {isSaving ? "Updating..." : "Update Project"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditProjectModal;
